/* eslint-disable */
// Session 55 · Admin console for Legal documents.
// - List all versions (grouped by kind)
// - View acceptance stats + recent acceptance log
// - Publish a new version (paste markdown)
// - Trigger reacceptance for the current version
//
// Route: /admin/legal (admin-only, gated by Protected adminOnly in App.js).

import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminLegal() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");   // overview | publish | history
  const [versions, setVersions] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  // Publish form
  const [publishForm, setPublishForm] = useState({
    kind: "terms",
    version: "",
    effective_date: new Date().toISOString().slice(0, 10),
    title: "",
    content_markdown: "",
    requires_reacceptance: true,
  });
  const [publishing, setPublishing] = useState(false);
  const [flash, setFlash] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [v, s, h] = await Promise.all([
        api.get("/admin/legal/versions"),
        api.get("/admin/legal/stats"),
        api.get("/admin/legal/history?limit=50"),
      ]);
      setVersions(v.data.versions || []);
      setStats(s.data);
      setHistory(h.data.acceptances || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (loading) return <div style={{padding:40}}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const submitPublish = async (e) => {
    e.preventDefault();
    if (!publishForm.version.trim() || publishForm.content_markdown.length < 200) {
      setErr("Version and content markdown (min 200 chars) are required.");
      return;
    }
    setPublishing(true); setErr(""); setFlash("");
    try {
      const r = await api.post("/admin/legal/publish", publishForm);
      setFlash(`Published ${r.data.version.kind} v${r.data.version.version}`);
      setPublishForm({...publishForm, content_markdown: "", version: ""});
      await load();
      setTab("overview");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const triggerReaccept = async (kind) => {
    if (!window.confirm(`Force ALL users to re-accept the current ${kind}? They'll see the modal on next page load.`)) return;
    try {
      const r = await api.post("/admin/legal/trigger-reacceptance", { kind });
      setFlash(`Cleared ${r.data.cleared_users} users' ${kind} acceptance — modal will appear next request.`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    }
  };

  const byKind = versions.reduce((acc, v) => {
    (acc[v.kind] = acc[v.kind] || []).push(v);
    return acc;
  }, {});

  return (
    <div style={{padding:"32px 40px", maxWidth:1100, margin:"0 auto", fontFamily:"Jost, sans-serif", color:"#1B2340"}} data-testid="admin-legal-page">
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16}}>
        <div>
          <span className="m-eyebrow" style={{color:"var(--gold)"}}>Admin · Legal</span>
          <h1 style={{fontFamily:"Cormorant Garamond, serif", fontSize:34, margin:"4px 0 0", fontWeight:500}}>Legal Documents</h1>
        </div>
        <Link to="/admin" data-testid="admin-legal-back" style={{color:"#B8913A"}}>← Admin</Link>
      </div>

      <div style={{display:"flex", gap:6, borderBottom:"1px solid rgba(184,145,58,0.25)", margin:"12px 0 24px"}} data-testid="admin-legal-tabs">
        {["overview","publish","history"].map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            data-testid={`admin-legal-tab-${t}`}
            style={{
              padding:"10px 18px", border:"none", background:"none", cursor:"pointer",
              fontWeight: tab===t?600:400, fontSize:13,
              borderBottom: tab===t?"2px solid #B8913A":"2px solid transparent",
              color: tab===t?"#1B2340":"rgba(27,35,64,0.6)",
            }}>{t[0].toUpperCase()+t.slice(1)}</button>
        ))}
      </div>

      {flash && <div style={{background:"#F0F7EE", border:"1px solid #83B26E", padding:"10px 14px", borderRadius:8, marginBottom:16, fontSize:13}} data-testid="admin-legal-flash">{flash}</div>}
      {err && <div style={{background:"#FDEDED", border:"1px solid #E5A0A0", padding:"10px 14px", borderRadius:8, marginBottom:16, fontSize:13}} data-testid="admin-legal-error">{err}</div>}

      {tab === "overview" && stats && (
        <div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12, marginBottom:24}} data-testid="admin-legal-stats">
            <StatCard label="Total users" value={stats.total_users} testid="stat-users" />
            <StatCard label="Users with any acceptance" value={stats.accepted_users} testid="stat-accepted" />
            <StatCard label="Up-to-date Terms" value={`${stats.up_to_date_terms}/${stats.total_users}`} testid="stat-terms" />
            <StatCard label="Up-to-date Privacy" value={`${stats.up_to_date_privacy}/${stats.total_users}`} testid="stat-privacy" />
            <StatCard label="Total acceptance receipts" value={stats.total_acceptances} testid="stat-receipts" />
          </div>

          {["terms","privacy"].map((kind) => (
            <div key={kind} style={{marginBottom:32}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
                <h2 style={{fontFamily:"Cormorant Garamond, serif", fontSize:22, margin:0}}>
                  {kind === "terms" ? "Terms of Service" : "Privacy Policy"}
                </h2>
                <button type="button" onClick={() => triggerReaccept(kind)}
                  data-testid={`trigger-reaccept-${kind}`}
                  style={{padding:"6px 14px", border:"1px solid rgba(27,35,64,0.3)", borderRadius:999, background:"transparent", cursor:"pointer", fontSize:12}}>
                  Force reacceptance
                </button>
              </div>
              <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}} data-testid={`admin-legal-versions-${kind}`}>
                <thead>
                  <tr style={{textAlign:"left", borderBottom:"1px solid rgba(184,145,58,0.25)"}}>
                    <th style={{padding:"8px 6px"}}>Version</th>
                    <th style={{padding:"8px 6px"}}>Effective</th>
                    <th style={{padding:"8px 6px"}}>Published</th>
                    <th style={{padding:"8px 6px"}}>Current</th>
                    <th style={{padding:"8px 6px"}}>Reacceptance</th>
                  </tr>
                </thead>
                <tbody>
                  {(byKind[kind] || []).map((v) => (
                    <tr key={v.version_id} style={{borderBottom:"1px solid rgba(27,35,64,0.06)"}}>
                      <td style={{padding:"8px 6px", fontWeight:500}}>v{v.version}</td>
                      <td style={{padding:"8px 6px"}}>{v.effective_date}</td>
                      <td style={{padding:"8px 6px", color:"rgba(27,35,64,0.6)"}}>{v.published_at?.slice(0,10)}</td>
                      <td style={{padding:"8px 6px"}}>{v.is_current ? <span style={{color:"#B8913A", fontWeight:600}}>● current</span> : <span style={{color:"rgba(27,35,64,0.4)"}}>retired</span>}</td>
                      <td style={{padding:"8px 6px"}}>{v.requires_reacceptance ? "yes" : "no"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {tab === "publish" && (
        <form onSubmit={submitPublish} data-testid="admin-legal-publish-form" style={{display:"grid", gap:14}}>
          <label style={{display:"grid", gap:4}}>
            <span style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>Document</span>
            <select value={publishForm.kind} onChange={(e) => setPublishForm({...publishForm, kind: e.target.value})}
              data-testid="publish-kind" style={inp}>
              <option value="terms">Terms of Service</option>
              <option value="privacy">Privacy Policy</option>
            </select>
          </label>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14}}>
            <label style={{display:"grid", gap:4}}>
              <span style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>Version (e.g. 1.1)</span>
              <input value={publishForm.version} onChange={(e) => setPublishForm({...publishForm, version: e.target.value})}
                required data-testid="publish-version" style={inp} />
            </label>
            <label style={{display:"grid", gap:4}}>
              <span style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>Effective date</span>
              <input type="date" value={publishForm.effective_date} onChange={(e) => setPublishForm({...publishForm, effective_date: e.target.value})}
                required data-testid="publish-effective-date" style={inp} />
            </label>
          </div>
          <label style={{display:"grid", gap:4}}>
            <span style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>Title (optional)</span>
            <input value={publishForm.title} onChange={(e) => setPublishForm({...publishForm, title: e.target.value})}
              data-testid="publish-title" style={inp} />
          </label>
          <label style={{display:"grid", gap:4}}>
            <span style={{fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase"}}>Content (Markdown)</span>
            <textarea value={publishForm.content_markdown} onChange={(e) => setPublishForm({...publishForm, content_markdown: e.target.value})}
              rows={20} required data-testid="publish-markdown"
              style={{...inp, fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", fontSize:12}} />
          </label>
          <label style={{display:"flex", gap:8, alignItems:"center", fontSize:13}}>
            <input type="checkbox" checked={publishForm.requires_reacceptance}
              onChange={(e) => setPublishForm({...publishForm, requires_reacceptance: e.target.checked})}
              data-testid="publish-requires-reaccept" />
            <span>Require existing users to re-accept</span>
          </label>
          <div>
            <button type="submit" disabled={publishing} data-testid="publish-submit"
              style={{padding:"10px 24px", background:"#B8913A", color:"#FAF8F3", border:"none", borderRadius:999, fontSize:13, fontWeight:500, cursor:"pointer"}}>
              {publishing ? "Publishing…" : "Publish new version"}
            </button>
          </div>
        </form>
      )}

      {tab === "history" && (
        <div data-testid="admin-legal-history">
          {history.length === 0 && <p style={{color:"rgba(27,35,64,0.6)", fontSize:13}}>No acceptances yet.</p>}
          <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
            <thead>
              <tr style={{textAlign:"left", borderBottom:"1px solid rgba(184,145,58,0.25)"}}>
                <th style={{padding:"8px 6px"}}>Accepted at</th>
                <th style={{padding:"8px 6px"}}>User</th>
                <th style={{padding:"8px 6px"}}>Terms</th>
                <th style={{padding:"8px 6px"}}>Privacy</th>
                <th style={{padding:"8px 6px"}}>Context</th>
                <th style={{padding:"8px 6px"}}>Platform</th>
                <th style={{padding:"8px 6px"}}>IP</th>
              </tr>
            </thead>
            <tbody>
              {history.map((a) => (
                <tr key={a.acceptance_id} style={{borderBottom:"1px solid rgba(27,35,64,0.06)"}}>
                  <td style={{padding:"6px 6px"}}>{a.accepted_at?.slice(0,19).replace("T"," ")}</td>
                  <td style={{padding:"6px 6px"}}>{a.user_id}</td>
                  <td style={{padding:"6px 6px"}}>v{a.accepted_versions?.terms?.version}</td>
                  <td style={{padding:"6px 6px"}}>v{a.accepted_versions?.privacy?.version}</td>
                  <td style={{padding:"6px 6px"}}>{a.context}</td>
                  <td style={{padding:"6px 6px"}}>{a.platform}</td>
                  <td style={{padding:"6px 6px", color:"rgba(27,35,64,0.55)"}}>{a.ip_address}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const inp = { border:"1px solid rgba(27,35,64,0.2)", padding:"9px 12px", borderRadius:8, fontSize:14 };

function StatCard({label, value, testid}) {
  return (
    <div style={{border:"1px solid rgba(184,145,58,0.25)", borderRadius:12, padding:"14px 16px", background:"rgba(184,145,58,0.04)"}} data-testid={testid}>
      <div style={{fontSize:11, textTransform:"uppercase", letterSpacing:"0.14em", color:"rgba(27,35,64,0.6)"}}>{label}</div>
      <div style={{fontSize:26, fontFamily:"Cormorant Garamond, serif", marginTop:4, color:"#1B2340"}}>{value}</div>
    </div>
  );
}
